import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet, TextInput, Alert, ActivityIndicator, ScrollView, Linking, Keyboard, KeyboardEvent } from 'react-native';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../types/navigation';
import { SocialLinks } from '../types/auth';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../context/AuthContext';
import ThemedText from '../components/common/ThemedText';
import { updateProfile } from '../lib/api/users';
import { storage } from '../utils/storage';
import { showSuccessToast } from '../utils/toast.native';
import { getUserProfileById } from '../lib/api/users';

type SocialLinksRouteProp = RouteProp<RootStackParamList, 'SocialLinks'>;
type SocialLinksNavigationProp = StackNavigationProp<RootStackParamList>;

interface SocialPlatform {
  key: keyof SocialLinks;
  name: string;
  icon: keyof typeof Ionicons.glyphMap;
  placeholder: string;
  color: string;
  domain: string[];
}

const SOCIAL_PLATFORMS: SocialPlatform[] = [
  {
    key: 'linkedin',
    name: 'LinkedIn',
    icon: 'logo-linkedin',
    placeholder: 'https://linkedin.com/in/username',
    color: '#0A66C2',
    domain: ['linkedin.com'],
  },
  {
    key: 'instagram',
    name: 'Instagram',
    icon: 'logo-instagram',
    placeholder: 'https://instagram.com/username',
    color: '#E4405F',
    domain: ['instagram.com'],
  },
  {
    key: 'facebook',
    name: 'Facebook',
    icon: 'logo-facebook',
    placeholder: 'https://facebook.com/username',
    color: '#1877F2',
    domain: ['facebook.com', 'fb.com'],
  },
  {
    key: 'youtube',
    name: 'YouTube',
    icon: 'logo-youtube',
    placeholder: 'https://youtube.com/@username',
    color: '#FF0000',
    domain: ['youtube.com', 'youtu.be'],
  },
  {
    key: 'x',
    name: 'X (Twitter)',
    icon: 'logo-twitter',
    placeholder: 'https://x.com/username',
    color: '#000000',
    domain: ['x.com', 'twitter.com'],
  },
];

const SocialLinksScreen: React.FC = () => {
  const route = useRoute<SocialLinksRouteProp>();
  const navigation = useNavigation<SocialLinksNavigationProp>();
  const { theme } = useTheme();
  const { user, reloadUser } = useAuth();
  const insets = useSafeAreaInsets();

  const targetUserId = route.params?.userId;
  const isOwnProfile = !targetUserId || targetUserId === user?.userId;

  const [loading, setLoading] = useState(!isOwnProfile);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({});
  const [isKeyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (isOwnProfile) {
      // Load own social links from user context
      setSocialLinks(user?.socialLinks || {});
      setLoading(false);
    } else {
      // Fetch other user's profile
      fetchUserProfile();
    }
  }, [targetUserId, user]);

  useEffect(() => {
    const showSub = Keyboard.addListener('keyboardDidShow', (event: KeyboardEvent) => {
      setKeyboardVisible(true);
      setKeyboardHeight(event.endCoordinates?.height || 0);
    });
    const hideSub = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardVisible(false);
      setKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      const token = await storage.getToken();
      if (!token) {
        Alert.alert('Error', 'Please login again');
        navigation.goBack();
        return;
      }

      const response = await getUserProfileById(token, targetUserId!);
      if (response.success && response.user) {
        setSocialLinks(response.user.socialLinks || {});
      } else {
        Alert.alert('Error', 'Profile not found');
        navigation.goBack();
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      Alert.alert('Error', 'Failed to load profile');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const validateUrl = (url: string, platform: SocialPlatform): { valid: boolean; cleanUrl: string; error?: string } => {
    if (!url.trim()) {
      return { valid: true, cleanUrl: '' };
    }

    // Remove leading/trailing spaces
    let cleanUrl = url.trim();

    // Add https:// if no protocol is present
    if (!cleanUrl.match(/^https?:\/\//i)) {
      cleanUrl = 'https://' + cleanUrl;
    }

    try {
      const urlObj = new URL(cleanUrl);
      const hostname = urlObj.hostname.toLowerCase().replace(/^www\./, '');

      // Check if domain matches the platform
      const domainMatches = platform.domain.some(domain => hostname.includes(domain));
      if (!domainMatches) {
        return {
          valid: false,
          cleanUrl,
          error: `Please enter a valid ${platform.name} URL (e.g., ${platform.placeholder})`,
        };
      }

      return { valid: true, cleanUrl };
    } catch (error) {
      return {
        valid: false,
        cleanUrl,
        error: `Please enter a valid URL for ${platform.name}`,
      };
    }
  };

  const handleLinkChange = (key: keyof SocialLinks, value: string) => {
    setSocialLinks(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    // Validate all URLs
    const validatedLinks: SocialLinks = {};
    for (const platform of SOCIAL_PLATFORMS) {
      const url = socialLinks[platform.key];
      if (url) {
        const validation = validateUrl(url, platform);
        if (!validation.valid) {
          Alert.alert('Invalid URL', validation.error || 'Please check your URLs');
          return;
        }
        validatedLinks[platform.key] = validation.cleanUrl;
      }
    }

    setSaving(true);
    try {
      const token = await storage.getToken();
      if (!token) {
        Alert.alert('Error', 'Please login again');
        return;
      }

      const response = await updateProfile(token, { socialLinks: validatedLinks });
      if (response.success) {
        await reloadUser();
        setIsEditing(false);
        showSuccessToast('Social links updated successfully');
      } else {
        Alert.alert('Error', response.message || 'Failed to update social links');
      }
    } catch (error) {
      console.error('Error updating social links:', error);
      Alert.alert('Error', 'Failed to update social links');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenLink = async (url: string) => {
    if (!url) return;

    try {
      const canOpen = await Linking.canOpenURL(url);
      if (canOpen) {
        await Linking.openURL(url);
      } else {
        Alert.alert('Error', 'Cannot open this URL');
      }
    } catch (error) {
      console.error('Error opening URL:', error);
      Alert.alert('Error', 'Failed to open link');
    }
  };

  const renderSocialLinkInput = (platform: SocialPlatform) => {
    const value = socialLinks[platform.key] || '';

    // View mode - only show links that have values
    if (!isEditing) {
      if (!value) return null;

      return (
        <TouchableOpacity
          key={platform.key}
          style={[styles.linkCard, { backgroundColor: theme.card, borderColor: theme.border }]}
          onPress={() => handleOpenLink(value)}
          activeOpacity={0.7}
        >
          <View style={[styles.iconContainer, { backgroundColor: platform.color + '15' }]}>
            <Ionicons name={platform.icon} size={24} color={platform.color} />
          </View>
          <View style={styles.linkContent}>
            <ThemedText style={[styles.platformName, { color: theme.text }]}>
              {platform.name}
            </ThemedText>
            <ThemedText style={[styles.linkUrl, { color: theme.textSecondary }]} numberOfLines={1}>
              {value}
            </ThemedText>
          </View>
          <Ionicons name="open-outline" size={20} color={theme.textTertiary} />
        </TouchableOpacity>
      );
    }

    // Edit mode - show all platforms with input fields
    return (
      <View key={platform.key} style={[styles.inputCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <View style={[styles.iconContainer, { backgroundColor: platform.color + '15' }]}>
          <Ionicons name={platform.icon} size={24} color={platform.color} />
        </View>
        <View style={styles.inputContent}>
          <ThemedText style={[styles.platformName, { color: theme.text }]}>
            {platform.name}
          </ThemedText>
          <TextInput
            style={[styles.input, { color: theme.text, borderColor: theme.border }]}
            value={value}
            onChangeText={(text) => handleLinkChange(platform.key, text)}
            placeholder={platform.placeholder}
            placeholderTextColor={theme.textTertiary}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="url"
          />
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={[styles.header, { backgroundColor: theme.background, borderBottomColor: theme.border, paddingTop: insets.top }]}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={28} color={theme.text} />
          </TouchableOpacity>
          <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Social Links</ThemedText>
          <View style={styles.headerRight} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      </View>
    );
  }

  const hasAnyLinks = SOCIAL_PLATFORMS.some(platform => socialLinks[platform.key]);
  const keyboardSpacerHeight = isKeyboardVisible ? Math.max(0, keyboardHeight) : 0;
  const scrollPadding = isKeyboardVisible ? 24 : insets.bottom + 24;

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      <View style={[styles.header, { backgroundColor: theme.background, borderBottomColor: theme.border, paddingTop: insets.top }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Social Links</ThemedText>
        <View style={styles.headerRight}>
          {isOwnProfile && !isEditing && (
            <TouchableOpacity
              onPress={() => setIsEditing(true)}
              style={[styles.headerIconButton, { borderColor: theme.border }]}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel="Edit social links"
            >
              <Ionicons name="create-outline" size={20} color={theme.primary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.content, { paddingBottom: scrollPadding }]}
        showsVerticalScrollIndicator={false}
      >
        {!hasAnyLinks && !isEditing ? (
          <View style={styles.emptyState}>
            <Ionicons name="link-outline" size={64} color={theme.textTertiary} />
            <ThemedText style={[styles.emptyTitle, { color: theme.text }]}>
              No Social Links
            </ThemedText>
            <ThemedText style={[styles.emptySubtitle, { color: theme.textSecondary }]}>
              {isOwnProfile ? "Click Edit to add your social media links" : "This user hasn't added any social media links yet"}
            </ThemedText>
          </View>
        ) : (
          <>
            {isOwnProfile && isEditing && (
              <View style={styles.infoSection}>
                <Ionicons name="information-circle-outline" size={20} color={theme.primary} />
                <ThemedText style={[styles.infoText, { color: theme.textSecondary }]}>
                  Add your social media profiles to help others connect with you
                </ThemedText>
              </View>
            )}

            <View style={styles.linksContainer}>
              {SOCIAL_PLATFORMS.map(platform => renderSocialLinkInput(platform))}
            </View>

            {isOwnProfile && isEditing && (
              <TouchableOpacity
                style={[styles.saveButtonLarge, { backgroundColor: theme.primary }]}
                onPress={handleSave}
                disabled={saving}
                activeOpacity={0.8}
              >
                {saving ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <>
                    <Ionicons name="checkmark-circle-outline" size={20} color="#FFFFFF" />
                    <ThemedText style={styles.saveButtonLargeText}>Save Changes</ThemedText>
                  </>
                )}
              </TouchableOpacity>
            )}
          </>
        )}

        {isKeyboardVisible && keyboardSpacerHeight > 0 && (
          <View style={[styles.keyboardSpacer, { height: keyboardSpacerHeight }]} />
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    fontFamily: 'ComicNeue_700Bold',
  },
  headerRight: {
    width: 70,
    alignItems: 'flex-end',
  },
  headerIconButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  infoSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
    gap: 12,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    fontFamily: 'ComicNeue_400Regular',
  },
  linksContainer: {
    gap: 16,
  },
  inputCard: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
  },
  linkCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  inputContent: {
    flex: 1,
    gap: 8,
  },
  linkContent: {
    flex: 1,
    gap: 4,
  },
  platformName: {
    fontSize: 16,
    fontWeight: '600',
    fontFamily: 'ComicNeue_700Bold',
  },
  input: {
    fontSize: 14,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    fontFamily: 'ComicNeue_400Regular',
  },
  linkUrl: {
    fontSize: 14,
    fontFamily: 'ComicNeue_400Regular',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    fontFamily: 'ComicNeue_700Bold',
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: 14,
    fontFamily: 'ComicNeue_400Regular',
    marginTop: 8,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  saveButtonLarge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    marginTop: 24,
    marginBottom: 40,
    gap: 8,
  },
  saveButtonLargeText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
  },
  keyboardSpacer: {
    width: '100%',
  },
});

export default SocialLinksScreen;
